import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, User, Phone, Mail, Plus } from 'lucide-react';
import { useCustomers } from '../../hooks/useCustomers';
import { CustomerFormModal } from '../../pages/Customers/components/CustomerFormModal';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';

interface CustomerSelectorProps {
  formData: {
    customerId: string;
    customerName: string;
    customerPhone: string;
    customerEmail: string;
  };
  onChange: (data: any) => void;
  onSubmit: () => void;
  submitLabel?: string;
  businessId?: string;
}

export function CustomerSelector({ formData, onChange, onSubmit, submitLabel, businessId }: CustomerSelectorProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewForm, setShowNewForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const { customers, loading, searchCustomers, refreshCustomers } = useCustomers(businessId);

  useEffect(() => {
    if (businessId) {

    }
  }, [businessId]);

  useEffect(() => {
    if (searchTerm) {
      // לוג לבדיקה

      const delaySearch = setTimeout(() => {
        searchCustomers(searchTerm);
      }, 300); // דיליי קטן למניעת חיפושים מיותרים

      return () => clearTimeout(delaySearch);
    }
  }, [searchTerm, searchCustomers]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit();
  };

  const handleCreateCustomer = async (data: any) => {
    if (!businessId) {
      toast.error('לא נמצא מזהה עסק');
      return;
    }

    setBusy(true);
    try {
      const { data: newCustomer, error } = await supabase
        .from('customers')
        .insert([{
          ...data,
          business_id: businessId,
          points: 0,
          diamonds: 0,
          loyalty_level: 'silver',
          loyalty_stats: {
            total_visits: 0,
            consecutive_visits: 0,
            last_visit: null,
            referrals: 0,
            total_spent: 0,
            achievements: []
          }
        }])
        .select()
        .single();

      if (error) throw error;

      toast.success('הלקוח נוסף בהצלחה');
      setShowNewForm(false);

      if (refreshCustomers) {
        refreshCustomers();
      }

      if (newCustomer) {
        onChange({
          ...formData,
          customerId: newCustomer.id,
          customerName: newCustomer.name,
          customerPhone: newCustomer.phone,
          customerEmail: newCustomer.email || ''
        });
      }
    } catch (error: any) {
      console.error('Error creating customer:', error);
      toast.error(error.message || 'שגיאה ביצירת לקוח');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {!showNewForm ? (
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              placeholder="חפש לפי שם או טלפון..."
            />
          </div>

          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {customers.length > 0 && (
            <div className="space-y-2">
              {customers.map((customer) => (
                <motion.button
                  key={customer.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="button"
                  onClick={() => {
                    onChange({
                      ...formData,
                      customerId: customer.id,
                      customerName: customer.name,
                      customerPhone: customer.phone,
                      customerEmail: customer.email
                    });
                    onSubmit();
                  }}
                  className="w-full flex items-center gap-4 p-4 bg-gray-50 rounded-xl text-right hover:bg-gray-100"
                >
                  <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                    <span className="text-lg font-medium text-indigo-600">
                      {customer.name.charAt(0)}
                    </span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium">{customer.name}</h3>
                    <p className="text-sm text-gray-500">{customer.phone}</p>
                  </div>
                </motion.button>
              ))}
            </div>
          )}

          {searchTerm && customers.length === 0 && !loading && (
            <div className="text-center py-8">
              <p className="text-gray-500 mb-4">לא נמצאו לקוחות</p>
              <button
                type="button"
                onClick={() => setShowNewForm(true)}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                הוסף לקוח חדש
              </button>
            </div>
          )}
        </div>
      ) : null}

      <div className="flex justify-end gap-4">
        {(submitLabel !== null && submitLabel !== undefined && submitLabel !== "") && (
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="submit"
            className="flex items-center gap-2 px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700"
          >
            <Plus className="h-5 w-5" />
            <span>{submitLabel}</span>
          </motion.button>
        )}
      </div>

      <CustomerFormModal
        isOpen={showNewForm}
        onSubmit={handleCreateCustomer}
        onCancel={() => setShowNewForm(false)}
        busy={busy}
      />
    </form>
  );
}